import { useState, useCallback, createContext, useContext, memo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import MenuItemDrawer from './MenuItemDrawer';
import { trackMenuItemView } from '../../utils/gtm';

// Create context for drawer state and actions
const MenuDrawerContext = createContext({
  openDrawer: () => {},
  closeDrawer: () => {},
  isDrawerOpen: false,
  drawerLoading: false,
  drawerError: null,
  getItemFromCache: () => null,
  getRelatedFromCache: () => null,
  menuItemsCache: {}
});

// Hook to use the drawer context
export const useMenuDrawer = () => useContext(MenuDrawerContext);

// Memoized drawer component that won't cause parent re-renders
const MemoizedDrawer = memo(function MemoizedDrawer({ 
  isOpen, 
  onClose, 
  menuItem, 
  loading, 
  error, 
  categorySlug, 
  relatedItems 
}) {
  return (
    <MenuItemDrawer
      isOpen={isOpen}
      onClose={onClose}
      menuItem={menuItem}
      loading={loading}
      error={error}
      categorySlug={categorySlug}
      relatedItems={relatedItems}
    />
  );
});

// Provider component that wraps the application
export default function MenuDrawerProvider({ children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [drawerLoading, setDrawerLoading] = useState(false);
  const [drawerError, setDrawerError] = useState(null);
  const [relatedItems, setRelatedItems] = useState([]);
  
  // Cache for menu items to reduce database calls
  const [menuItemsCache, setMenuItemsCache] = useState({});
  const [relatedItemsCache, setRelatedItemsCache] = useState({});

  // Function to open the drawer immediately with loading state
  const openDrawer = useCallback((item, category, related = [], itemSlug = null) => {
    // If we already have item data, use it
    if (item) {
      setSelectedItem(item);
      setSelectedCategory(category);
      setRelatedItems(related);
      
      // Track menu item view in GTM
      trackMenuItemView(
        item.name,
        item.slug,
        item.category?.name || category || 'Unknown',
        item.calories
      );
      
      // Cache the item data for future use
      if (item._id) {
        setMenuItemsCache(prev => ({
          ...prev,
          [item.slug]: item
        }));
        
        // Cache related items if available
        if (related && related.length > 0) {
          setRelatedItemsCache(prev => ({
            ...prev,
            [item._id]: related
          }));
        }
      }
      
      // Update URL with search parameter
      if (itemSlug) {
        const searchParams = new URLSearchParams(location.search);
        searchParams.set('item', itemSlug);
        const newUrl = `${location.pathname}?${searchParams.toString()}`;
        
        // Use both window.history.pushState and navigate for maximum compatibility
        window.history.pushState({}, '', newUrl);
        
        // Also use navigate to ensure React Router's internal state is updated
        // Use replace: true to avoid adding to history stack
        navigate(newUrl, { 
          replace: true,
          state: location.state // Preserve any existing state
        });
      }
    }
    
    // Open the drawer immediately, even if we're still loading
    setDrawerOpen(true);
  }, [location, navigate]);

  // Function to start loading state
  const startLoading = useCallback(() => {
    setDrawerLoading(true);
    setDrawerError(null);
  }, []);

  // Function to set error
  const setError = useCallback((error) => {
    setDrawerError(error);
    setDrawerLoading(false);
  }, []);

  // Function to finish loading
  const finishLoading = useCallback(() => {
    setDrawerLoading(false);
  }, []);

  // Function to close the drawer and update URL
  const closeDrawer = useCallback(() => {
    // First set drawer to closed
    setDrawerOpen(false);
    
    // Reset selected item and related items
    setSelectedItem(null);
    setRelatedItems([]);
    
    // Get the current URL search params
    const searchParams = new URLSearchParams(location.search);
    
    // If we have an item parameter, remove it
    if (searchParams.has('item')) {
      // Remove the item parameter
      searchParams.delete('item');
      
      // Create a new URL without the item parameter
      let newUrl = location.pathname;
      if (searchParams.toString()) {
        newUrl += `?${searchParams.toString()}`;
      }
      
      // Use both window.history.replaceState and navigate for maximum compatibility
      window.history.replaceState({}, '', newUrl);
      
      // Also use navigate with replace to update the URL without adding to history
      // This ensures React Router's internal state is updated
      navigate(newUrl, { 
        replace: true,
        state: location.state // Preserve any existing state
      });
    }
  }, [location, navigate]);

  // Function to get an item from cache
  const getItemFromCache = useCallback((itemSlug) => {
    return menuItemsCache[itemSlug] || null;
  }, [menuItemsCache]);
  
  // Function to get related items from cache
  const getRelatedFromCache = useCallback((itemId) => {
    return relatedItemsCache[itemId] || null;
  }, [relatedItemsCache]);
  
  // Context value
  const contextValue = {
    openDrawer,
    closeDrawer,
    startLoading,
    setError,
    finishLoading,
    isDrawerOpen: drawerOpen,
    drawerLoading,
    drawerError,
    selectedItem,
    selectedCategory,
    relatedItems,
    getItemFromCache,
    getRelatedFromCache,
    menuItemsCache
  };

  return (
    <MenuDrawerContext.Provider value={contextValue}>
      {children}
      <MemoizedDrawer
        isOpen={drawerOpen}
        onClose={closeDrawer}
        menuItem={selectedItem}
        loading={drawerLoading}
        error={drawerError}
        categorySlug={selectedCategory}
        relatedItems={relatedItems}
      />
    </MenuDrawerContext.Provider>
  );
}
